import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, Popconfirm, Form } from 'antd';

import { ActionBox, Space, Box } from '@/features/constructorCommon/styled';
import { ScoringRiskCategoryForAnswer } from '@/features/constructor';
import { ConstructorForm } from '@/components/admin/molecules';

export const Answer = ({
  answerProp,
  questionId,
  onCreated,
  handleCreate,
  handleUpdate,
  handleDelete,
  isCreating,
  isUpdating,
  isDeleting,
  type,
  isScoring,
  autoCheck
}) => {
  const [answer, setAnswer] = useState(answerProp || {});
  const { t } = useTranslation();
  const [errorPoints, setErrorPoints] = useState(false);
  const [errorScoringPoints, setErrorScoringPoints] = useState(false);
  const [isModalRiskCategoriesOpened, setIsModalRiskCategoriesOpened] = useState(false);

  useEffect(() => {
    if (!answerProp?.id) return;

    setAnswer(answerProp);
  }, [answerProp]);

  const handleAction = useCallback(() => {
    if (
      isScoring &&
      answer.pointsScoring &&
      !answer.pointsScoring.match(/^(?:[0-9]|[1-9][0-9]|[1-9][0-9][0-9])$/)
    ) {
      if (!errorScoringPoints) setErrorScoringPoints(true);
      return;
    } else if (autoCheck && answer.points && !answer.points.match(/^[0-1]$/)) {
      if (!errorPoints) setErrorPoints(true);
    } else {
      if (errorScoringPoints) setErrorScoringPoints(false);
      if (errorPoints) setErrorPoints(false);
    }
    if (answer.id) {
      handleUpdate(answer);
    } else {
      handleCreate(answer, questionId).then((res) => {
        setAnswer({});
        onCreated && onCreated();
      });
    }
  }, [answer]);

  const onDelete = () => {
    handleDelete(answer);
  };

  const isDisabled =
    !answer.answer ||
    (answer.answer === answerProp?.answer &&
      answer.answer_RU === answerProp?.answer_RU &&
      answer.answer_LV === answerProp?.answer_LV &&
      answer.sortOrder === answerProp?.sortOrder &&
      answer.pointsScoring === answerProp?.pointsScoring);

  return (
    <Box
      $isNew={!answer.id}
      styles={{
        body: {
          padding: '16px'
        }
      }}
    >
      <ConstructorForm>
        <div>
          <Form.Item label={t('Answer')}>
            <Input
              name='answer'
              value={answer.answer}
              onChange={(e) => setAnswer({ ...answer, answer: e.target.value })}
            />
          </Form.Item>
          {type === 'products' && (
            <>
              <Form.Item label={`${t('Answer')} (RU)`}>
                <Input
                  name='answer_RU'
                  value={answer.answer_RU}
                  onChange={(e) => setAnswer({ ...answer, answer_RU: e.target.value })}
                />
              </Form.Item>
              <Form.Item label={`${t('Answer')} (LV)`}>
                <Input
                  name='answer_RU'
                  value={answer.answer_LV}
                  onChange={(e) => setAnswer({ ...answer, answer_LV: e.target.value })}
                />
              </Form.Item>
            </>
          )}
          {answer.id && (
            <Form.Item label={t('Order')}>
              <Input
                name='sortOrder'
                value={answer.sortOrder}
                style={{ width: '100px' }}
                onChange={(e) => setAnswer({ ...answer, sortOrder: e.target.value })}
              />
            </Form.Item>
          )}
          {autoCheck && (
            <>
              <Form.Item label={t('Points (0 or 1, for autoCheck questionnaire)')}>
                <Input
                  name='points'
                  value={answer.points}
                  type='number'
                  min={0}
                  max={1}
                  step={1}
                  style={{ width: '100px' }}
                  onChange={(e) => setAnswer({ ...answer, points: e.target.value })}
                />
              </Form.Item>
              {errorPoints && (
                <span
                  style={{
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('Only integer numbers 0 or 1')}
                </span>
              )}
            </>
          )}

          {isScoring && (
            <>
              <Space />
              <Form.Item label={t('Points scoring (0-999, for isScoring questionnaire)')}>
                <Input
                  name='pointsScoring'
                  value={answer.pointsScoring}
                  type='number'
                  min={0}
                  max={999}
                  step={1}
                  style={{ width: '100px' }}
                  onChange={(e) => setAnswer({ ...answer, pointsScoring: e.target.value })}
                />
              </Form.Item>
              {errorScoringPoints && (
                <span
                  style={{
                    color: 'red',
                    fontSize: '12px'
                  }}
                >
                  {t('Only integer numbers from 0 to 999')}
                </span>
              )}
              <Space />

              <Button onClick={() => setIsModalRiskCategoriesOpened(true)} type='primary'>
                {!answer.categoryScoringId ? t('Add risk category') : t('Update risk category')}
              </Button>

              <Modal
                title={t('Add risk categories')}
                width={700}
                style={{
                  top: 20
                }}
                okText={'Add'}
                footer={null}
                open={isModalRiskCategoriesOpened}
                onCancel={() => setIsModalRiskCategoriesOpened(false)}
              >
                <ScoringRiskCategoryForAnswer
                  answer={answer}
                  closeModal={() => setIsModalRiskCategoriesOpened(false)}
                />
              </Modal>
            </>
          )}
          <ActionBox>
            <Button
              onClick={handleAction}
              disabled={isDisabled}
              type='primary'
              loading={isCreating || isUpdating}
            >
              {answer.id ? t('Update Answer') : t('Create Answer')}
            </Button>

            {answer.id && (
              <Popconfirm
                placement='topRight'
                title={t('Are you sure to delete this answer?')}
                onConfirm={onDelete}
                okText={t('Yes')}
                cancelText={t('No')}
              >
                <Button danger loading={isDeleting}>
                  {t('Delete Answer')}
                </Button>
              </Popconfirm>
            )}
          </ActionBox>
        </div>
      </ConstructorForm>
    </Box>
  );
};
